/** Alineado con Spaces.Domain.Model.CompressionAxis */
export enum CompressionAxis {
  Width = 1,
  Depth = 2,
  Height = 3,
}

const DEFAULT_OFFSET_MIN_PRD_SEP_X = 0;
const COMPRESSION_EPS = 0.01;
const SHELF_FIT_EPS = 0.5;

const _round = (value: number, places = 2): number => {
  const factor = Math.pow(10, places);
  return Math.round(Number(value) * factor) / factor;
};

export type ProductCompressionChangeType = 'add_facing' | 'add_product' | 'orientation_change';

export interface ProductCompressionProdUpdate {
  prodId: string | number;
  appliedCompressionPercent: number | null;
  appliedCompressionAxis: CompressionAxis | null;
  facings?: number;
  rotation?: number;
  rotateY?: boolean;
  stacksLyingDown?: boolean;
}

export interface ProductCompressionAttemptResult {
  ok: boolean;
  needsCompression: boolean;
  updates?: ProductCompressionProdUpdate[];
  blockingProductNames?: string[];
  appliedPercent?: number;
}

export interface ProductCompressionAttemptParams {
  shelfProds: any[];
  shelfWidth: number;
  shelfType?: string;
  changeType: ProductCompressionChangeType;
  targetProdId?: string | number;
  facingDelta?: number;
  newProd?: any;
  orientationOverride?: Partial<{
    rotation: number;
    rotateY: boolean;
    stacksLyingDown: boolean;
  }>;
}

export const isRealFixedEmptySpace = (prd: any): boolean =>
  !!(
    prd?.isFixedEmptySpace ||
    prd?.itemData?.isFixedEmptySpace ||
    Number(prd?.itemData?.itemId) === -2 ||
    prd?.itemData?.code === 'FixedEmptySpace'
  );

export const getMaxCompressionPercent = (prod: any): number | null => {
  const raw = prod?.itemData?.maxCompressionPercent;
  if (raw == null || raw <= 0) return null;
  return Number(raw);
};

export const isCompressibleProd = (prod: any): boolean => {
  const max = getMaxCompressionPercent(prod);
  return max != null && max > 0;
};

/** Eje de compresión según orientación exhibida (D-22). */
export const getCompressionAxisForProd = (prod: any): CompressionAxis => {
  if (prod?.rotateY) return CompressionAxis.Depth;
  if (prod?.stacksLyingDown) return CompressionAxis.Height;
  return CompressionAxis.Width;
};

export const getCompressedDimension = (baseDimension: number, compressionPercent: number): number =>
  baseDimension * (1 - compressionPercent / 100);

export const getCompressionWidthFactor = (prod: any): number => {
  const percent = prod?.appliedCompressionPercent;
  if (percent == null || percent <= 0) return 1;
  return 1 - Number(percent) / 100;
};

/** Dimensión UOM base sobre el eje comprimible (sin rotación de layout). */
export const getBaseExhibitedDimension = (prod: any, axis?: CompressionAxis): number => {
  const size = prod?.itemData?.size || {};
  const resolvedAxis = axis ?? getCompressionAxisForProd(prod);
  if (resolvedAxis === CompressionAxis.Depth) return Number(size.d ?? size.w ?? 0);
  if (resolvedAxis === CompressionAxis.Height) return Number(size.h ?? 0);
  return Number(size.w ?? 0);
};

/** Ancho lineal por frente en el estante, sin compresión aplicada. */
export const getLinearWidthPerFacing = (prod: any): number => {
  if (!prod?.itemData?.size) return 0;
  if (isRealFixedEmptySpace(prod)) return Number(prod.fixedWidth ?? prod.facings ?? 0);

  const size = prod.itemData.size;
  let w = !(prod.rotation % 180) ? size.w : size.h;
  if (prod.rotateY) w = size.d || size.w;
  if (prod.stacksLyingDown) w = size.h;

  return Number(w) || 0;
};

export const getProdTotalLinearWidth = (prod: any, compressionPercent: number | null = null): number => {
  if (isRealFixedEmptySpace(prod)) return Number(prod.fixedWidth ?? prod.facings ?? 0);

  const facings = Math.max(1, Number(prod?.facings) || 1);
  let perFacing = getLinearWidthPerFacing(prod);

  if (compressionPercent != null && compressionPercent > 0) {
    perFacing = getCompressedDimension(perFacing, compressionPercent);
  }

  // Mismo redondeo por frente que el layout visual (_auxCalcGroupImgItemsAndTotalSize).
  return _round(perFacing) * facings;
};

export const getShelfUsedLinearWidth = (prods: any[], uniformPercent: number | null = null, ignorePersistedCompression = false): number => {
  const sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;
  let total = 0;

  (prods || []).forEach((prd, index) => {
    const appliedPercent =
      uniformPercent != null && isCompressibleProd(prd) ? Math.min(uniformPercent, getMaxCompressionPercent(prd)!) : null;
    const effectivePercent = ignorePersistedCompression
      ? appliedPercent
      : appliedPercent ?? (prd?.appliedCompressionPercent != null ? Number(prd.appliedCompressionPercent) : null);
    total += getProdTotalLinearWidth(prd, effectivePercent);
    if (index < (prods?.length || 0) - 1) total += sepX;
  });

  return total;
};

export const getShelfRemainingWidth = (
  shelfWidth: number,
  prods: any[],
  uniformPercent: number | null = null,
  ignorePersistedCompression = false
): number => shelfWidth - getShelfUsedLinearWidth(prods, uniformPercent, ignorePersistedCompression);

export const getShelfUsedLinearWidthWithIndividualMax = (prods: any[]): number => {
  const sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;
  let total = 0;

  (prods || []).forEach((prd, index) => {
    const max = getMaxCompressionPercent(prd);
    total += getProdTotalLinearWidth(prd, max);
    if (index < (prods?.length || 0) - 1) total += sepX;
  });

  return total;
};

const buildUpdatesForIndividualMax = (prods: any[]): ProductCompressionProdUpdate[] =>
  (prods || []).map(prod => {
    const max = getMaxCompressionPercent(prod);
    const appliedPercent = max != null && max > 0 ? _round(max, 4) : null;

    return {
      prodId: prod.id,
      appliedCompressionPercent: appliedPercent,
      appliedCompressionAxis: appliedPercent != null ? getCompressionAxisForProd(prod) : null,
      facings: prod.facings,
      rotation: prod.rotation,
      rotateY: prod.rotateY,
      stacksLyingDown: prod.stacksLyingDown,
    };
  });

const cloneProdWithOverrides = (prod: any, overrides: Record<string, any> = {}): any => ({
  ...prod,
  ...overrides,
  itemData: prod?.itemData,
});

const buildProdsAfterChange = (params: ProductCompressionAttemptParams): any[] => {
  const { shelfProds, changeType, targetProdId, facingDelta = 1, newProd, orientationOverride } = params;

  if (changeType === 'add_product' && newProd) {
    return [...(shelfProds || []), cloneProdWithOverrides(newProd, { id: newProd.id || '__new__' })];
  }

  return (shelfProds || []).map(prod => {
    if (String(prod?.id) !== String(targetProdId)) return prod;

    if (changeType === 'add_facing') {
      return cloneProdWithOverrides(prod, { facings: Math.max(1, (Number(prod.facings) || 1) + facingDelta) });
    }

    if (changeType === 'orientation_change' && orientationOverride) {
      return cloneProdWithOverrides(prod, orientationOverride);
    }

    return prod;
  });
};

const getRequiredExtraWidth = (beforeProds: any[], afterProds: any[]): number => {
  const before = getShelfUsedLinearWidth(beforeProds, null);
  const after = getShelfUsedLinearWidth(afterProds, null);
  return Math.max(0, after - before);
};

const getProdDisplayName = (prod: any): string =>
  prod?.itemData?.name || prod?.itemData?.code || prod?.itemData?.itemCode || String(prod?.id ?? '');

/** Busca el mínimo p* uniforme (capado por maxCompressionPercent de cada ítem). */
export const findMinimumUniformCompressionPercent = (prods: any[], shelfWidth: number): number | null => {
  const compressible = (prods || []).filter(isCompressibleProd);
  if (!compressible.length) return null;

  const fits = (percent: number): boolean => getShelfUsedLinearWidth(prods, percent) <= shelfWidth + SHELF_FIT_EPS;

  if (fits(0)) return 0;

  let lo = 0;
  let hi = 100;
  let best: number | null = null;

  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) {
      best = mid;
      hi = mid;
    } else {
      lo = mid;
    }
  }

  if (best == null && fits(100)) best = 100;
  return best;
};

const buildUpdatesForPercent = (prods: any[], percent: number | null): ProductCompressionProdUpdate[] =>
  (prods || []).map(prod => {
    const appliedPercent = percent != null && isCompressibleProd(prod) ? Math.min(percent, getMaxCompressionPercent(prod)!) : null;

    return {
      prodId: prod.id,
      appliedCompressionPercent: appliedPercent != null && appliedPercent > 0 ? _round(appliedPercent, 4) : null,
      appliedCompressionAxis: appliedPercent != null && appliedPercent > 0 ? getCompressionAxisForProd(prod) : null,
      facings: prod.facings,
      rotation: prod.rotation,
      rotateY: prod.rotateY,
      stacksLyingDown: prod.stacksLyingDown,
    };
  });

const buildUpdatesClearingCompression = (prods: any[], afterProds: any[]): ProductCompressionProdUpdate[] => {
  const afterById = new Map(afterProds.map(p => [String(p.id), p]));

  return (prods || []).map(prod => {
    const after = afterById.get(String(prod.id)) || prod;
    return {
      prodId: prod.id,
      appliedCompressionPercent: null,
      appliedCompressionAxis: null,
      facings: after.facings,
      rotation: after.rotation,
      rotateY: after.rotateY,
      stacksLyingDown: after.stacksLyingDown,
    };
  });
};

const buildUpdatesForAfterProds = (beforeProds: any[], afterProds: any[]): ProductCompressionProdUpdate[] => {
  const updates = buildUpdatesClearingCompression(beforeProds, afterProds).map(u => {
    const after = afterProds.find(p => String(p.id) === String(u.prodId));
    if (!after) return u;
    return { ...u, facings: after.facings, rotation: after.rotation, rotateY: after.rotateY, stacksLyingDown: after.stacksLyingDown };
  });

  afterProds.forEach(after => {
    if (!updates.some(u => String(u.prodId) === String(after.id))) {
      updates.push({
        prodId: after.id,
        appliedCompressionPercent: null,
        appliedCompressionAxis: null,
        facings: after.facings,
        rotation: after.rotation,
        rotateY: after.rotateY,
        stacksLyingDown: after.stacksLyingDown,
      });
    }
  });

  return updates;
};

/** Calcula compresión necesaria para un estado de estante (CATMAN-S54: reconcile en layout). */
export const computeShelfCompressionForProds = (
  afterProds: any[],
  shelfWidth: number,
  beforeProds: any[] = afterProds
): ProductCompressionAttemptResult => {
  // Ignorar compresión persistida: decidir si cabe a ancho UOM real, no al ancho ya comprimido (CATMAN-S54).
  const remainingAfterNoCompression = getShelfRemainingWidth(shelfWidth, afterProds, null, true);

  if (remainingAfterNoCompression >= -COMPRESSION_EPS) {
    return { ok: true, needsCompression: false, updates: buildUpdatesForAfterProds(beforeProds, afterProds) };
  }

  const fitsIndividualMax = getShelfUsedLinearWidthWithIndividualMax(afterProds) <= shelfWidth + SHELF_FIT_EPS;
  if (fitsIndividualMax) {
    const maxPercents = (afterProds || [])
      .filter(isCompressibleProd)
      .map(getMaxCompressionPercent)
      .filter(v => v != null) as number[];
    const appliedPercent = maxPercents.length ? Math.max(...maxPercents) : undefined;

    return {
      ok: true,
      needsCompression: true,
      appliedPercent,
      updates: buildUpdatesForIndividualMax(afterProds),
    };
  }

  const pStar = findMinimumUniformCompressionPercent(afterProds, shelfWidth);

  if (pStar == null || getShelfUsedLinearWidth(afterProds, pStar) > shelfWidth + SHELF_FIT_EPS) {
    const blocking = afterProds.filter(p => !isRealFixedEmptySpace(p) && !isCompressibleProd(p)).map(getProdDisplayName);
    return { ok: false, needsCompression: true, blockingProductNames: blocking };
  }

  if (pStar <= COMPRESSION_EPS) {
    return { ok: true, needsCompression: false, updates: buildUpdatesForAfterProds(beforeProds, afterProds) };
  }

  const blockingItems = afterProds
    .filter(p => isCompressibleProd(p) && pStar > getMaxCompressionPercent(p)! + COMPRESSION_EPS)
    .map(getProdDisplayName);

  if (blockingItems.length) {
    return { ok: false, needsCompression: true, blockingProductNames: blockingItems };
  }

  return {
    ok: true,
    needsCompression: true,
    appliedPercent: pStar,
    updates: buildUpdatesForPercent(afterProds, pStar),
  };
};

export const applyCompressionUpdatesToProds = (prods: any[], updates: ProductCompressionProdUpdate[] = []): any[] => {
  const updatesById = new Map((updates || []).map(u => [String(u.prodId), u]));

  return (prods || []).map(prod => {
    const update = updatesById.get(String(prod.id));
    if (!update) return prod;

    return {
      ...prod,
      appliedCompressionPercent: update.appliedCompressionPercent,
      appliedCompressionAxis: update.appliedCompressionAxis,
      ...(update.facings != null ? { facings: update.facings } : {}),
      ...(update.rotation != null ? { rotation: update.rotation } : {}),
      ...(update.rotateY != null ? { rotateY: update.rotateY } : {}),
      ...(update.stacksLyingDown != null ? { stacksLyingDown: update.stacksLyingDown } : {}),
    };
  });
};

/** Reconcilia compresión en cada recálculo de layout (CATMAN-S54). */
export const resolveShelfCompressionForLayout = (prods: any[], shelfWidth: number, shelfType = 'normalShelf-level'): any[] => {
  if (shelfType !== 'normalShelf-level' || !(shelfWidth > 0) || !prods?.length) return prods || [];

  const result = computeShelfCompressionForProds(prods, shelfWidth);
  if (!result.ok || !result.updates?.length) return prods;

  return applyCompressionUpdatesToProds(prods, result.updates);
};

export const tryShelfProductCompression = (params: ProductCompressionAttemptParams): ProductCompressionAttemptResult => {
  const { shelfProds, shelfWidth, shelfType = 'normalShelf-level' } = params;

  if (shelfType !== 'normalShelf-level' || !(shelfWidth > 0)) {
    return { ok: true, needsCompression: false };
  }

  const beforeProds = shelfProds || [];
  const afterProds = buildProdsAfterChange(params);

  if (getShelfRemainingWidth(shelfWidth, afterProds, null, true) >= -COMPRESSION_EPS) {
    return { ok: true, needsCompression: false, updates: buildUpdatesForAfterProds(beforeProds, afterProds) };
  }

  return computeShelfCompressionForProds(afterProds, shelfWidth, beforeProds);
};
